import React, { useCallback, useEffect, useRef, useState } from "react";
import { useStreaming } from "@/lib/streaming";
import { Move, Maximize2, FlipHorizontal2 } from "lucide-react";

/**
 * PipOverlayControls
 * -----------------------------------------------------------------------------
 * Streamer-only draggable + resizable overlay that maps 1:1 onto the outgoing
 * composited canvas. The provider's compositor loop reads `cameraOverlay` from
 * state, so any drag/resize here shows up in real time on the actual outgoing
 * stream (and on every viewer's screen — same track).
 *
 * `previewEl` is the <video> element rendering the composite feed. This box
 * positions itself against that element so the streamer gets WYSIWYG editing.
 */
export default function PipOverlayControls({ previewEl }) {
  const { pipMode, cameraOverlay, setCameraOverlay } = useStreaming();
  const [rect, setRect] = useState({ left: 0, top: 0, width: 0, height: 0 });
  const dragRef = useRef(null); // { mode: "move" | "resize", startX, startY, startOv }

  // Compute pixel rect of the preview element so we can overlay in absolute
  // page coordinates. Re-measures on window resize + when the preview mounts.
  const measure = useCallback(() => {
    if (!previewEl) return;
    const r = previewEl.getBoundingClientRect();
    setRect({ left: r.left, top: r.top, width: r.width, height: r.height });
  }, [previewEl]);

  useEffect(() => {
    if (!pipMode || !previewEl) return undefined;
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(previewEl);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    // Continuous re-measure while dragging (video may resize as data arrives)
    const iv = setInterval(measure, 500);
    return () => { ro.disconnect(); window.removeEventListener("resize", measure); window.removeEventListener("scroll", measure, true); clearInterval(iv); };
  }, [pipMode, previewEl, measure]);

  const onPointerDown = (mode) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = {
      mode,
      startX: e.clientX,
      startY: e.clientY,
      startOv: { ...cameraOverlay },
    };
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp, { once: true });
  };

  const onPointerMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d || !rect.width) return;
    const dx = (e.clientX - d.startX) / rect.width;
    const dy = (e.clientY - d.startY) / rect.height;
    if (d.mode === "move") {
      const nx = Math.max(0, Math.min(1 - d.startOv.w, d.startOv.x + dx));
      const ny = Math.max(0, Math.min(1 - d.startOv.h, d.startOv.y + dy));
      setCameraOverlay({ ...d.startOv, x: nx, y: ny });
    } else if (d.mode === "resize") {
      // Preserve aspect ratio (~4:3) while resizing from the bottom-right handle
      const newW = Math.max(0.08, Math.min(0.7, d.startOv.w + dx));
      const newH = newW * (d.startOv.h / d.startOv.w);
      const clampedH = Math.max(0.08, Math.min(0.7, newH));
      const clampedW = clampedH * (d.startOv.w / d.startOv.h);
      const finalW = Math.min(clampedW, 1 - d.startOv.x);
      const finalH = Math.min(clampedH, 1 - d.startOv.y);
      setCameraOverlay({ ...d.startOv, w: finalW, h: finalH });
    }
  }, [rect, setCameraOverlay]);

  const onPointerUp = () => {
    dragRef.current = null;
    window.removeEventListener("pointermove", onPointerMove);
  };

  const toggleMirror = () => setCameraOverlay({ ...cameraOverlay, mirror: !cameraOverlay.mirror });

  if (!pipMode || !previewEl || rect.width === 0) return null;

  // Overlay in pixel coords derived from fractional cameraOverlay
  const box = {
    left: rect.left + cameraOverlay.x * rect.width,
    top: rect.top + cameraOverlay.y * rect.height,
    width: cameraOverlay.w * rect.width,
    height: cameraOverlay.h * rect.height,
  };

  return (
    <>
      {/* The draggable box itself */}
      <div
        data-testid="pip-overlay-box"
        style={{ position: "fixed", left: box.left, top: box.top, width: box.width, height: box.height, zIndex: 40 }}
        className="pointer-events-none"
      >
        <div
          onPointerDown={onPointerDown("move")}
          className="absolute inset-0 border-2 border-dashed border-[color:var(--sc-cyan)] rounded-md cursor-move pointer-events-auto"
          style={{ boxShadow: "0 0 0 1px rgba(0,229,255,0.4), 0 0 24px rgba(0,229,255,0.25)" }}
          title="Drag to move your camera"
        />
        {/* Resize handle (bottom-right) */}
        <div
          data-testid="pip-overlay-resize"
          onPointerDown={onPointerDown("resize")}
          className="absolute -bottom-1.5 -right-1.5 w-4 h-4 bg-[color:var(--sc-cyan)] rounded-sm cursor-se-resize border border-white pointer-events-auto"
          title="Drag to resize your camera"
        />
        {/* Little label */}
        <div className="absolute -top-6 left-0 flex items-center gap-2 bg-black/70 text-white text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-sm border border-white/20 pointer-events-none">
          <Move className="w-3 h-3" />
          Camera PiP
        </div>
      </div>

      {/* Floating toolbar (fixed to viewport bottom-right) */}
      <div
        data-testid="pip-overlay-toolbar"
        style={{ position: "fixed", right: 20, bottom: 20, zIndex: 45 }}
        className="flex items-center gap-2 bg-black/85 border border-[color:var(--sc-cyan)]/40 rounded-md p-2 shadow-2xl backdrop-blur-sm"
      >
        <div className="text-[10px] uppercase tracking-widest sc-mono text-[color:var(--sc-cyan)] mr-1">Camera PiP</div>
        <button
          data-testid="pip-overlay-preset-tr"
          onClick={() => setCameraOverlay({ ...cameraOverlay, x: 1 - cameraOverlay.w - 0.02, y: 0.02 })}
          className="sc-btn-ghost text-[10px] px-2 py-1"
          title="Top right"
        >TR</button>
        <button
          data-testid="pip-overlay-preset-tl"
          onClick={() => setCameraOverlay({ ...cameraOverlay, x: 0.02, y: 0.02 })}
          className="sc-btn-ghost text-[10px] px-2 py-1"
          title="Top left"
        >TL</button>
        <button
          data-testid="pip-overlay-preset-br"
          onClick={() => setCameraOverlay({ ...cameraOverlay, x: 1 - cameraOverlay.w - 0.02, y: 1 - cameraOverlay.h - 0.02 })}
          className="sc-btn-ghost text-[10px] px-2 py-1"
          title="Bottom right"
        >BR</button>
        <button
          data-testid="pip-overlay-preset-bl"
          onClick={() => setCameraOverlay({ ...cameraOverlay, x: 0.02, y: 1 - cameraOverlay.h - 0.02 })}
          className="sc-btn-ghost text-[10px] px-2 py-1"
          title="Bottom left"
        >BL</button>
        <div className="w-px h-5 bg-white/10 mx-1" />
        <button
          data-testid="pip-overlay-size-sm"
          onClick={() => setCameraOverlay({ ...cameraOverlay, w: 0.18, h: 0.18 * (9 / 16) })}
          className="sc-btn-ghost text-[10px] px-2 py-1"
          title="Small"
        >S</button>
        <button
          data-testid="pip-overlay-size-md"
          onClick={() => setCameraOverlay({ ...cameraOverlay, w: 0.28, h: 0.28 * (9 / 16) })}
          className="sc-btn-ghost text-[10px] px-2 py-1"
          title="Medium"
        >M</button>
        <button
          data-testid="pip-overlay-size-lg"
          onClick={() => setCameraOverlay({ ...cameraOverlay, w: 0.42, h: 0.42 * (9 / 16) })}
          className="sc-btn-ghost text-[10px] px-2 py-1"
          title="Large"
        >L</button>
        <div className="w-px h-5 bg-white/10 mx-1" />
        <button
          data-testid="pip-overlay-mirror"
          onClick={toggleMirror}
          className={`text-[10px] px-2 py-1 rounded-sm border flex items-center gap-1 ${cameraOverlay.mirror ? "border-[color:var(--sc-cyan)] text-[color:var(--sc-cyan)]" : "border-white/15 text-zinc-300"}`}
          title="Mirror camera horizontally"
        ><FlipHorizontal2 className="w-3 h-3" /> Mirror</button>
        <Maximize2 className="w-3 h-3 text-zinc-500 ml-1" />
      </div>
    </>
  );
}
